/**
 * RC-MOBILE-AUTH-REPAIR-001 · Stage 1a · Pure deep-link parser
 * ============================================================
 *
 * Fixes the fragment-loss bug in NativeBridge.appUrlOpen that caused
 * Google mobile sign-in to lose `session_id` between the OAuth
 * broker's redirect and the AuthCallback screen.
 *
 * Contract:
 *   parseDeepLink(rawUrl) → {
 *     valid:  boolean,
 *     reason: "empty" | "not_url" | "wrong_scheme" |
 *             "wrong_host" | "oversize" | "unsafe_scheme",
 *     hash:   string,          // ready to assign to window.location.hash
 *     params: URLSearchParams, // typed view (session_id, code, state, ...)
 *   }
 *
 * Design invariants:
 *   · Pure function — no window, no side effects. Identical output on
 *     Android and iOS given the same input string.
 *   · Rejects: non-cruisecommand: schemes, javascript:, data:, file:,
 *     URLs > 4 KB, unknown hosts, empty input.
 *   · Preserves BOTH query and fragment parameters, merged into a
 *     single hash string. Fragment wins on key collision (RFC 3986:
 *     fragment is the innermost target).
 *   · Emits `#session_id=…&code=…` form so AuthCallback's existing
 *     URLSearchParams parse (page reads window.location.hash) recovers
 *     every parameter without any route-token stripping.
 */

const APPROVED_SCHEME = "cruisecommand:";
const APPROVED_HOSTS  = new Set(["auth-callback"]);
const MAX_URL_BYTES   = 4 * 1024;
const UNSAFE_SCHEMES  = new Set(["javascript:", "data:", "file:", "vbscript:"]);

export function parseDeepLink(rawUrl) {
  if (typeof rawUrl !== "string" || rawUrl.length === 0) {
    return { valid: false, reason: "empty" };
  }
  if (rawUrl.length > MAX_URL_BYTES) {
    return { valid: false, reason: "oversize" };
  }

  let url;
  try {
    url = new URL(rawUrl);
  } catch {
    return { valid: false, reason: "not_url" };
  }

  const scheme = (url.protocol || "").toLowerCase();
  if (UNSAFE_SCHEMES.has(scheme)) {
    return { valid: false, reason: "unsafe_scheme" };
  }
  if (scheme !== APPROVED_SCHEME) {
    return { valid: false, reason: "wrong_scheme" };
  }

  // Custom-scheme URLs put the "auth-callback" token in host.
  const host = (url.hostname || url.host || "").toLowerCase();
  if (!APPROVED_HOSTS.has(host)) {
    return { valid: false, reason: "wrong_host" };
  }

  // Merge query + fragment into one params bag. RFC 3986: the
  // fragment identifies the sub-resource so its params take precedence
  // on key collision.
  const merged = new URLSearchParams();
  if (url.search && url.search.length > 1) {
    for (const [k, v] of new URLSearchParams(url.search.slice(1))) {
      merged.set(k, v);
    }
  }
  if (url.hash && url.hash.length > 1) {
    for (const [k, v] of new URLSearchParams(url.hash.slice(1))) {
      merged.set(k, v); // fragment wins
    }
  }

  const encoded = merged.toString();
  return {
    valid:  true,
    hash:   encoded ? `#${encoded}` : "",
    params: merged,
  };
}

/**
 * Duplicate-delivery guard for OAuth deep links. Capacitor can deliver the
 * same callback twice (retained launch URL + appUrlOpen, or a second tap on
 * the provider's "return to app" page). The provider handles (`session_id`
 * for the Google broker, `state` for code flows) are single-use, so a
 * repeat must never re-drive the callback screen against a consumed handle.
 */
export function createDeepLinkDeliveryGuard() {
  const delivered = new Set();
  return {
    /** true → first delivery of this OAuth handle; false → duplicate or no handle. */
    accept(params) {
      const handle = params?.get("session_id") || params?.get("state") || "";
      if (!handle) return false;
      if (delivered.has(handle)) return false;
      delivered.add(handle);
      return true;
    },
    reset() { delivered.clear(); },
  };
}

export default parseDeepLink;
